"use client";

import type { useSpring } from "motion/react";
import type { RefObject } from "react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { type SpringConfig, useChartConfig } from "../chart-config-context";
import { chartCssVars } from "../chart-context";
import { autoContrastColor as autoContrastForegroundColor } from "../resolve-css-color";
import {
  FloatingTooltip,
  type FloatingTooltipPlacement,
} from "./floating-tooltip";

export interface TooltipBoxProps {
  /** X position in pixels (relative to container) */
  x: number;
  /** Y position in pixels (relative to container) */
  y: number;
  /** Whether the tooltip is visible */
  visible: boolean;
  /** Container ref for portal rendering */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Container width for flip detection */
  containerWidth: number;
  /** Container height for bounds clamping */
  containerHeight: number;
  /** Offset from the target position */
  offset?: number;
  /** Custom class name */
  className?: string;
  /** Tooltip content */
  children: React.ReactNode;
  /** Override left position (bypasses internal calculation) */
  left?: number | ReturnType<typeof useSpring>;
  /** Override top position (bypasses internal calculation) */
  top?: number | ReturnType<typeof useSpring>;
  /** Force flip direction (for custom positioning) */
  flipped?: boolean;
  /** Per-chart override; falls back to `ChartConfigProvider.tooltipBoxSpring`. */
  springConfig?: SpringConfig;
  /** Animate panel position with a spring. Default: true */
  animate?: boolean;
  /** Fade/scale the panel on show. Default: true */
  entrance?: boolean;
  /** Inline styles for the inner tooltip panel. */
  panelStyle?: React.CSSProperties;
  /**
   * Tooltip panel background color (CSS variable or color value).
   * Default: `var(--chart-tooltip-background)`.
   */
  backgroundColor?: string;
  placement?: FloatingTooltipPlacement;
}

// Inner-only-on-visible so the position spring restarts from the current
// pointer position instead of gliding in from the previous hover.
export function TooltipBox(props: TooltipBoxProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const container = props.containerRef.current;
  if (!(mounted && container)) {
    return null;
  }
  if (!props.visible) {
    return null;
  }
  return <TooltipBoxInner {...props} container={container} />;
}

function TooltipBoxInner({
  x,
  y,
  containerWidth,
  containerHeight,
  offset = 16,
  className = "",
  children,
  left: leftOverride,
  top: topOverride,
  flipped: flippedOverride,
  springConfig,
  animate = true,
  entrance = true,
  panelStyle,
  backgroundColor = chartCssVars.tooltipBackground,
  placement,
  container,
}: Omit<TooltipBoxProps, "visible" | "containerRef"> & {
  container: HTMLElement;
}) {
  const { tooltipBoxSpring } = useChartConfig();
  const effectiveSpring = springConfig ?? tooltipBoxSpring;

  const panelClassName = cn(
    "min-w-[140px] overflow-hidden rounded-sm border border-[var(--chart-tooltip-border)] text-chart-tooltip-foreground shadow-none",
    panelStyle?.backgroundColor === undefined &&
      backgroundColor === chartCssVars.tooltipBackground &&
      "bg-chart-tooltip-background",
    panelStyle?.backdropFilter === undefined && "backdrop-blur-sm"
  );

  const autoContrastColor =
    backgroundColor !== chartCssVars.tooltipBackground
      ? autoContrastForegroundColor(backgroundColor)
      : null;

  const panelStyleResolved = {
    ...(panelStyle?.backgroundColor === undefined && {
      backgroundColor,
    }),
    ...(autoContrastColor && {
      color: autoContrastColor,
      "--chart-tooltip-foreground": autoContrastColor,
      "--chart-tooltip-muted": autoContrastColor,
    }),
    ...panelStyle,
  } as React.CSSProperties;

  return (
    <FloatingTooltip
      anchor={{
        mode: "container",
        container,
        height: containerHeight,
        offset,
        placement,
        width: containerWidth,
        x,
        y,
      }}
      animatePosition={animate}
      className={cn("z-50", className)}
      entrance={entrance}
      flipped={flippedOverride}
      left={leftOverride}
      panelClassName={panelClassName}
      panelStyle={panelStyleResolved}
      springConfig={effectiveSpring}
      top={topOverride}
      visible
    >
      {children}
    </FloatingTooltip>
  );
}

TooltipBox.displayName = "TooltipBox";

export default TooltipBox;
